import { getAnalyticsIdentity } from './analytics-identity';
import type { PrivacyConsentSync } from '@tabbio/utils/analytics/consent';
import { api } from './api';
import type { PrivacyPreferences } from './privacy-preferences';

/**
 * Reports the signed-in user's privacy decision to the API.
 *
 * The device keeps the decision; the server keeps a copy so that events it
 * emits with no browser attached (RevenueCat webhooks, lifecycle hooks) can
 * respect it. Called from the session trackers in the root layouts whenever the
 * user is authenticated and the decision has been hydrated or changes.
 *
 * Deduplicated per user and payload for the life of the process, so a layout
 * re-render is free. Fire and forget from the caller's point of view: a failed
 * sync is retried on the next change or launch, never surfaced to the user.
 */

let lastSyncedKey: string | null = null;
let pendingSync = Promise.resolve(false);
const observedDecisionUsers = new Set<string>();

export function buildPrivacyConsentSyncBody(
  preferences: PrivacyPreferences,
): PrivacyConsentSync {
  return {
    noticeVersion: preferences.noticeVersion,
    analytics: preferences.analytics,
    marketing: preferences.marketing,
    decidedAt: preferences.decidedAt,
    updatedAt: preferences.updatedAt ?? new Date().toISOString(),
    source: preferences.source,
  };
}

/** Nothing to report until the person has answered at least one purpose. */
export function hasPrivacyDecision(preferences: PrivacyPreferences) {
  return (
    preferences.analytics !== 'unknown' || preferences.marketing !== 'unknown'
  );
}

async function performPrivacyConsentSync(
  userId: string,
  preferences: PrivacyPreferences,
): Promise<boolean> {
  if (!userId || (!hasPrivacyDecision(preferences) && !observedDecisionUsers.has(userId))) {
    return false;
  }

  const body = buildPrivacyConsentSyncBody(preferences);
  const nativeIdentity =
    preferences.analytics === 'granted' ? await getAnalyticsIdentity() : null;
  if (nativeIdentity) body.nativeIdentity = nativeIdentity;
  const key = `${userId}:${JSON.stringify(body)}`;
  if (key === lastSyncedKey) {
    return false;
  }

  try {
    const response = await api.put('/api/users/me/privacy-consent', body);
    if (response.error) {
      return false;
    }
    lastSyncedKey = key;
    return true;
  } catch {
    return false;
  }
}

/** Preserve decision order even when identity lookup or the network is slow. */
export function syncPrivacyConsentToServer(
  userId: string,
  preferences: PrivacyPreferences,
): Promise<boolean> {
  if (userId && hasPrivacyDecision(preferences)) observedDecisionUsers.add(userId);
  pendingSync = pendingSync
    .catch(() => false)
    .then(() => performPrivacyConsentSync(userId, preferences));
  return pendingSync;
}

export function resetPrivacyConsentSyncForTests() {
  pendingSync = Promise.resolve(false);
  lastSyncedKey = null;
  observedDecisionUsers.clear();
}
